import type { PodUsageData } from '@/shared/types/workspace';
import { formatCount, formatMonthDay, formatPodHours } from './format';

export type UsageTone = 'accent' | 'danger' | 'info';

export type UsageSummary = {
  isFree: boolean;
  label: string;
  used: string;
  limit: string;
  left: string;
  extra: string;
  ratio: number;
  percentUsed: number;
  meterTone: UsageTone;
  isNearlyOut: boolean;
  isOut: boolean;
  isPastIncluded: boolean;
  resetsOn: string | null;
};

const NEARLY_OUT_RATIO = 0.8;

/** Everything a meter or a usage line shows, derived once (Foundations: used, then limit, then reset). */
export function summarizeUsage(usage: PodUsageData): UsageSummary {
  const isFree = usage.isOneTimeGrant;
  const included = usage.includedPodHours;
  const ratio = included > 0 ? usage.usedPodHours / included : 0;
  const isOut = isFree && ratio >= 1;
  const isPastIncluded = !isFree && usage.extraPodHours > 0;
  const remaining = Math.max(0, included - usage.usedPodHours);
  return {
    isFree,
    label: isFree ? 'Free Pod-hours' : 'Pod-hours this month',
    used: formatPodHours(usage.usedPodHours, isFree ? 2 : 1),
    limit: formatCount(included),
    left: isFree ? formatCount(Math.round(remaining * 100) / 100) : formatPodHours(remaining),
    extra: formatPodHours(usage.extraPodHours),
    ratio,
    percentUsed: Math.floor(ratio * 100),
    meterTone: isOut ? 'danger' : isPastIncluded ? 'info' : 'accent',
    isNearlyOut: isFree && ratio >= NEARLY_OUT_RATIO && !isOut,
    isOut,
    isPastIncluded,
    resetsOn: usage.resetsAt ? formatMonthDay(usage.resetsAt) : null,
  };
}
